import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Wrench, Camera, Loader2, Check, Trash2, Pencil, Send, Copy, Sparkles, Lock, Plus, ClipboardList, ImagePlus,
  Share2, Ban, Eye, CheckCircle2, MessageSquareWarning, FileClock,
} from "lucide-react";
import { toast } from "sonner";
import { RoomAssign } from "@/components/RoomAssign";
import { RepairCaseFile } from "@/components/RepairCaseFile";

const SEV = {
  low: { label: "Low", cls: "bg-sky-400/15 text-sky-200 border-sky-400/30" },
  moderate: { label: "Moderate", cls: "bg-amber-400/15 text-amber-100 border-amber-400/30" },
  urgent: { label: "Urgent", cls: "bg-red-500/15 text-red-200 border-red-500/40" },
};
const STATUS = {
  open: { label: "Open", cls: "bg-white/10 text-white/70 border-white/20" },
  landlord_responded: { label: "Landlord responded", cls: "bg-sky-400/15 text-sky-200 border-sky-400/30" },
  resolved: { label: "Resolved", cls: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30" },
  unresolved: { label: "Unresolved", cls: "bg-red-500/15 text-red-200 border-red-500/40" },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function MaintenanceLog({ propertyId, readOnly = false, onFollowupCount }) {
  const { isPremium } = useTier();
  const [issues, setIssues] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [linkEvents, setLinkEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editIssue, setEditIssue] = useState(null);
  const [noticeFor, setNoticeFor] = useState(null);
  const [followupFor, setFollowupFor] = useState(null);
  const [caseFileFor, setCaseFileFor] = useState(null);
  const fileRef = useRef(null);

  const loadShare = useCallback(async () => {
    try {
      const [{ data: links }, { data: events }] = await Promise.all([
        api.get(`/share-links/${propertyId}`),
        api.get(`/properties/${propertyId}/link-events`),
      ]);
      setShareLinks((links || []).filter((l) => l.link_type === "maintenance_view"));
      setLinkEvents(events || []);
    } catch (e) { console.error("load maintenance share failed", e); }
  }, [propertyId]);

  const load = useCallback(async () => {
    if (!isPremium) { setLoading(false); return; }
    try {
      const { data } = await api.get(`/properties/${propertyId}/maintenance`);
      setIssues(data);
      await loadShare();
    } catch (e) { console.error("load maintenance failed", e); }
    finally { setLoading(false); }
  }, [propertyId, isPremium, loadShare]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onFollowupCount) onFollowupCount(issues.filter((i) => i.needs_followup).length);
  }, [issues, onFollowupCount]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAdding(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/maintenance`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIssues((cur) => [data, ...cur]);
      setEditIssue(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that issue.");
    } finally { setAdding(false); }
  };

  const toggleResolved = async (issue) => {
    try {
      const { data } = await api.patch(`/maintenance/${issue.issue_id}`, { resolved: !issue.resolved_at });
      setIssues((cur) => cur.map((i) => (i.issue_id === data.issue_id ? data : i)));
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update."); }
  };

  const remove = async (issue) => {
    try {
      await api.delete(`/maintenance/${issue.issue_id}`);
      setIssues((cur) => cur.filter((i) => i.issue_id !== issue.issue_id));
      toast.success("Issue removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't remove."); }
  };

  if (!isPremium) return (
    <PremiumLock title="Maintenance Follow-Up" feature="maintenance" icon={Wrench} testId="maintenance-locked"
      description="Track repairs from report to resolution with a dated evidence trail and polite follow-up drafts." />
  );
  if (loading) return null;

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="maintenance-log">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="maintenance-file-input" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><Wrench size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Maintenance log</h3>
            <p className="text-sm text-white/60">Document repair issues with dated photos during your tenancy — a record that protects your deposit from charges for problems you reported.</p>
          </div>
        </div>
        {!readOnly && (
          <Button size="sm" data-testid="maintenance-add-btn" disabled={adding} onClick={() => fileRef.current?.click()}
            className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
            {adding ? <Loader2 size={15} className="animate-spin" /> : <><Camera size={15} className="mr-1.5" /> Add issue</>}
          </Button>
        )}
      </div>

      {readOnly && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/50" data-testid="maintenance-readonly">
          <Lock size={12} /> This property is locked — the log is read-only.
        </p>
      )}

      {issues.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="maintenance-empty">No maintenance issues logged yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {!readOnly && issues.filter((i) => !i.assigned_room).length > 0 && (
            <p className="text-[11px] text-white/60 inline-flex items-center gap-1.5" data-testid="maintenance-unassigned-count">
              {issues.filter((i) => !i.assigned_room).length} issue{issues.filter((i) => !i.assigned_room).length === 1 ? "" : "s"} not assigned to a room — assign one to include it in Condition History.
            </p>
          )}
          {issues.map((issue) => {
            const sev = SEV[issue.severity] || SEV.moderate;
            const photo = issue.photos?.[0];
            return (
              <div key={issue.issue_id} data-testid={`maintenance-issue-${issue.issue_id}`}
                className="flex gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                {photo && (
                  <a href={fileUrl(photo.storage_path)} target="_blank" rel="noopener noreferrer"
                    className="h-16 w-16 rounded-lg overflow-hidden border border-white/10 shrink-0" data-testid={`maintenance-photo-${issue.issue_id}`}>
                    <img src={fileUrl(photo.storage_path)} alt={issue.title} className="h-full w-full object-cover" />
                  </a>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-semibold ${issue.resolved_at ? "line-through text-white/45" : ""}`}>{issue.title}</p>
                    <span className={`inline-flex items-center rounded-full border text-[11px] font-semibold px-2 py-0.5 ${sev.cls}`} data-testid={`maintenance-severity-${issue.issue_id}`}>{sev.label}</span>
                    <span className={`inline-flex items-center rounded-full border text-[11px] font-semibold px-2 py-0.5 ${(STATUS[issue.status] || STATUS.open).cls}`} data-testid={`maintenance-status-${issue.issue_id}`}>{(STATUS[issue.status] || STATUS.open).label}</span>
                    {issue.photos?.length > 1 && <span className="text-[11px] text-white/45">+{issue.photos.length - 1} photo</span>}
                  </div>
                  {issue.description && <p className="text-xs text-white/60 mt-0.5">{issue.description}</p>}
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/45 items-center">
                    {issue.reported_to_landlord_at
                      ? <span className="text-emerald-300" data-testid={`maintenance-reported-${issue.issue_id}`}>Reported {fmt(issue.reported_to_landlord_at)}</span>
                      : <span>Not yet reported</span>}
                    {!issue.resolved_at && issue.days_open != null && (
                      <span data-testid={`maintenance-daysopen-${issue.issue_id}`}>Open {issue.days_open}d</span>
                    )}
                    {issue.resolved_at && <span className="text-emerald-300">Resolved {fmt(issue.resolved_at)}</span>}
                    {issue.needs_followup && (
                      <span data-testid={`maintenance-followup-chip-${issue.issue_id}`}
                        className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-400/15 text-amber-100 text-[11px] font-semibold px-2 py-0.5">
                        No response in {issue.days_since_contact} days
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`maintenance-notify-btn-${issue.issue_id}`}
                      onClick={() => setNoticeFor(issue)}
                      className="rounded-lg h-8 border-white/20 bg-white/5 text-white hover:bg-white/15">
                      <Send size={13} className="mr-1.5" /> Notify landlord
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`maintenance-casefile-btn-${issue.issue_id}`}
                      onClick={() => setCaseFileFor(issue)}
                      className="rounded-lg h-8 border-white/20 bg-white/5 text-white hover:bg-white/15">
                      <FileClock size={13} className="mr-1.5" /> Case file
                    </Button>
                    {issue.needs_followup && (
                      <Button size="sm" variant="outline" data-testid={`maintenance-followup-btn-${issue.issue_id}`}
                        onClick={() => setFollowupFor(issue)}
                        className="rounded-lg h-8 border-amber-400/40 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20">
                        <Send size={13} className="mr-1.5" /> Send follow-up
                      </Button>
                    )}
                    {!readOnly && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`maintenance-resolve-btn-${issue.issue_id}`}
                          onClick={() => toggleResolved(issue)}
                          className="rounded-lg h-8 border-white/20 bg-white/5 text-white hover:bg-white/15">
                          <Check size={13} className="mr-1.5" /> {issue.resolved_at ? "Reopen" : "Resolved"}
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`maintenance-edit-btn-${issue.issue_id}`}
                          onClick={() => setEditIssue(issue)} className="rounded-lg h-8 w-8 p-0 text-white/70 hover:bg-white/10"><Pencil size={13} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`maintenance-delete-btn-${issue.issue_id}`}
                          onClick={() => remove(issue)} className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40"><Trash2 size={13} /></Button>
                      </>
                    )}
                  </div>
                  <IssueTrail issue={issue} readOnly={readOnly} onFollowup={() => setFollowupFor(issue)}
                    onUpdated={(u) => setIssues((cur) => cur.map((i) => (i.issue_id === u.issue_id ? u : i)))} />
                  {!readOnly && (
                    <RoomAssign propertyId={propertyId} evidenceType="maintenance" evidenceId={issue.issue_id}
                      assignedRoom={issue.assigned_room}
                      onChanged={(room) => setIssues((cur) => cur.map((i) => i.issue_id === issue.issue_id ? { ...i, assigned_room: room } : i))} />
                  )}
                  {!readOnly && (
                    <IssueShare issue={issue}
                      link={shareLinks.find((l) => l.item_ref === issue.issue_id && l.status === "active")}
                      events={linkEvents.filter((e) => e.item_ref === issue.issue_id)}
                      onChanged={loadShare} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editIssue && (
        <EditIssueDialog issue={editIssue} onClose={() => setEditIssue(null)}
          onSaved={(u) => { setIssues((cur) => cur.map((i) => (i.issue_id === u.issue_id ? u : i))); setEditIssue(null); }} />
      )}
      {noticeFor && (
        <NoticeDialog issue={noticeFor} onClose={() => setNoticeFor(null)}
          onReported={(u) => setIssues((cur) => cur.map((i) => (i.issue_id === u.issue_id ? u : i)))} />
      )}
      {followupFor && (
        <FollowupDialog issue={followupFor} onClose={() => setFollowupFor(null)}
          onDone={(u) => setIssues((cur) => cur.map((i) => (i.issue_id === u.issue_id ? u : i)))} />
      )}
      {caseFileFor && (
        <RepairCaseFile issueId={caseFileFor.issue_id} readOnly={readOnly}
          onClose={() => setCaseFileFor(null)} onChanged={load} />
      )}
    </div>
  );
}

function EditIssueDialog({ issue, onClose, onSaved }) {
  const [title, setTitle] = useState(issue.title || "");
  const [description, setDescription] = useState(issue.description || "");
  const [severity, setSeverity] = useState(issue.severity || "moderate");
  const [status, setStatus] = useState(issue.status || "open");
  const [responseNote, setResponseNote] = useState(issue.landlord_response_note || "");
  const [saving, setSaving] = useState(false);
  const aiProposed = issue.ai_suggested && !issue.reported_to_landlord_at;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/maintenance/${issue.issue_id}`, {
        title: title.trim() || "Maintenance issue", description: description.trim(), severity,
        status, landlord_response_note: responseNote.trim(),
      });
      toast.success("Issue saved");
      onSaved(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="maintenance-edit-dialog">
        <DialogHeader>
          <DialogTitle>Confirm the issue details</DialogTitle>
          <DialogDescription>
            {aiProposed ? "We suggested the details below from your photo — correct anything, then save." : "Update this maintenance issue."}
          </DialogDescription>
        </DialogHeader>
        {aiProposed && (
          <p className="inline-flex items-center gap-1.5 text-xs text-brand -mt-1"><Sparkles size={12} /> AI-suggested — you're in control</p>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input data-testid="maintenance-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Severity</Label>
            <select data-testid="maintenance-severity-select" value={severity} onChange={(e) => setSeverity(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              <option value="low">Low — minor / cosmetic</option>
              <option value="moderate">Moderate — needs repair</option>
              <option value="urgent">Urgent — safety / active damage</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea data-testid="maintenance-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="mt-1 rounded-xl" placeholder="What is the problem?" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select data-testid="maintenance-status-select" value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              <option value="open">Open — awaiting landlord</option>
              <option value="landlord_responded">Landlord responded</option>
              <option value="resolved">Resolved</option>
              <option value="unresolved">Unresolved — no action taken</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Landlord response note (optional)</Label>
            <Textarea data-testid="maintenance-response-input" value={responseNote} onChange={(e) => setResponseNote(e.target.value)} rows={2} className="mt-1 rounded-xl" placeholder="What did the landlord say?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="maintenance-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="maintenance-edit-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoticeDialog({ issue, onClose, onReported }) {
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let active = true;
    api.post(`/maintenance/${issue.issue_id}/notice`)
      .then(({ data }) => { if (active) setNotice(data); })
      .catch((err) => { toast.error(err?.response?.data?.detail || "Couldn't draft the notice."); onClose(); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [issue.issue_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markReported = async () => {
    try {
      const { data } = await api.post(`/maintenance/${issue.issue_id}/reported`);
      onReported(data);
    } catch (e) { console.error("mark reported failed", e); }
  };

  const body = notice?.letter?.letter_body_markdown || "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(body); toast.success("Notice copied"); await markReported(); }
    catch (e) { toast.error("Couldn't copy"); }
  };
  const mailto = () => {
    const to = notice?.landlord_email || "";
    const subject = `Maintenance request — ${notice?.property_address || "my rental"}`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    markReported();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="maintenance-notice-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Notify your landlord</DialogTitle>
          <DialogDescription>A courteous repair request — facts and dates only. Review, then copy or email it.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-[#64748B]"><Loader2 size={18} className="animate-spin" /> Drafting your notice…</div>
        ) : (
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#0F172A] whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="maintenance-notice-preview">{body}</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="maintenance-notice-close">Close</Button>
          <Button variant="outline" onClick={copy} disabled={loading} className="rounded-xl" data-testid="maintenance-notice-copy"><Copy size={15} className="mr-1.5" /> Copy text</Button>
          <Button onClick={mailto} disabled={loading} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="maintenance-notice-email"><Send size={15} className="mr-1.5" /> Email landlord</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowupDialog({ issue, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let active = true;
    api.post(`/maintenance/${issue.issue_id}/followup`)
      .then(({ data }) => { if (active) { setNotice(data); if (data.issue) onDone(data.issue); } })
      .catch((err) => { toast.error(err?.response?.data?.detail || "Couldn't draft the follow-up."); onClose(); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [issue.issue_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = notice?.letter?.letter_body_markdown || "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(body); toast.success("Follow-up copied"); }
    catch (e) { toast.error("Couldn't copy"); }
  };
  const mailto = () => {
    const to = notice?.landlord_email || "";
    const subject = `Following up: maintenance request — ${notice?.property_address || "my rental"}`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="maintenance-followup-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send a follow-up</DialogTitle>
          <DialogDescription>A courteous check-in on a previously reported issue — facts and dates only. Review, then copy or email it.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-[#64748B]"><Loader2 size={18} className="animate-spin" /> Drafting your follow-up…</div>
        ) : (
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#0F172A] whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="maintenance-followup-preview">{body}</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="maintenance-followup-close">Close</Button>
          <Button variant="outline" onClick={copy} disabled={loading} className="rounded-xl" data-testid="maintenance-followup-copy"><Copy size={15} className="mr-1.5" /> Copy text</Button>
          <Button onClick={mailto} disabled={loading} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="maintenance-followup-email"><Send size={15} className="mr-1.5" /> Email landlord</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function IssueShare({ issue, link, events, onChanged }) {
  const [busy, setBusy] = useState(false);
  const url = link ? `${window.location.origin}/maintenance/${link.slug}` : null;
  const responses = (events || []).filter((e) => e.event_type === "item_acknowledged" || e.event_type === "noted");

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/share-links`, { property_id: issue.property_id, link_type: "maintenance_view", issue_id: issue.issue_id });
      await onChanged();
      try { await navigator.clipboard.writeText(data.share_url); toast.success("Share link created & copied"); }
      catch { toast.success("Share link created"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create link"); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Copied!"); } catch { toast.error("Copy failed"); } };
  const revoke = async () => {
    try { await api.post(`/share-links/${link.link_id}/revoke`); await onChanged(); toast.success("Link revoked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't revoke"); }
  };

  const fmtD = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

  if (!link) {
    return (
      <div className="mt-3 border-t border-white/10 pt-3" data-testid={`maintenance-share-${issue.issue_id}`}>
        <Button size="sm" variant="outline" disabled={busy} onClick={create}
          data-testid={`maintenance-share-btn-${issue.issue_id}`}
          className="rounded-lg h-8 border-white/20 bg-white/5 text-white hover:bg-white/15">
          {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Share2 size={13} className="mr-1.5" />} Share issue
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid={`maintenance-share-${issue.issue_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/70"><Share2 size={12} /> Shared issue link</span>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={copy} data-testid={`maintenance-share-copy-${issue.issue_id}`} className="rounded-lg h-7 w-7 p-0 border-white/20 bg-white/5 text-white hover:bg-white/15"><Copy size={12} /></Button>
          <Button size="sm" variant="ghost" onClick={revoke} data-testid={`maintenance-share-revoke-${issue.issue_id}`} className="rounded-lg h-7 text-[#FCA5A5] hover:bg-[#7F1D1D]/40 text-xs"><Ban size={12} className="mr-1" /> Revoke</Button>
        </div>
      </div>
      <p className="text-[11px] font-mono truncate text-white/50" data-testid={`maintenance-share-url-${issue.issue_id}`}>{url}</p>
      <div className="text-xs" data-testid={`maintenance-share-status-${issue.issue_id}`}>
        {!link.first_viewed_at ? (
          <span className="inline-flex items-center gap-1.5 text-white/45"><Eye size={12} /> Not viewed yet</span>
        ) : responses.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-sky-200"><Eye size={12} /> Viewed by recipient {fmtD(link.first_viewed_at)} · No response recorded yet</span>
        ) : (
          <div className="space-y-1">
            {responses.map((r) => (
              <div key={r.event_id} className="flex items-start gap-1.5 text-white/80">
                {r.event_type === "item_acknowledged"
                  ? <CheckCircle2 size={12} className="text-emerald-300 mt-0.5 shrink-0" />
                  : <MessageSquareWarning size={12} className="text-amber-200 mt-0.5 shrink-0" />}
                <span>{r.signer_name || "Recipient"} {r.event_type === "item_acknowledged" ? "acknowledged" : "added a note"} · {fmtD(r.created_at)}{r.note ? ` — "${r.note}"` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function IssueTrail({ issue, readOnly, onUpdated, onFollowup }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const afterRef = useRef(null);
  const updates = issue.updates || [];
  const after = issue.photos_after_resolved || [];

  const addUpdate = async () => {
    const n = note.trim();
    if (!n) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/maintenance/${issue.issue_id}/updates`, { note: n });
      setNote(""); onUpdated(data); toast.success("Update logged");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't log update."); }
    finally { setBusy(false); }
  };

  const addAfterPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file); form.append("phase", "after");
      const { data } = await api.post(`/maintenance/${issue.issue_id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(data); toast.success("Resolution photo added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add photo."); }
    finally { setUploading(false); }
  };

  return (
    <div className="mt-3 border-t border-white/10 pt-3 space-y-3" data-testid={`maintenance-trail-${issue.issue_id}`}>
      {issue.landlord_response_note && (
        <p className="text-xs text-white/70" data-testid={`maintenance-response-${issue.issue_id}`}>
          <span className="text-white/45">Landlord response: </span>{issue.landlord_response_note}
        </p>
      )}
      {issue.needs_followup && !readOnly && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2" data-testid={`maintenance-followup-banner-${issue.issue_id}`}>
          <span className="text-xs text-amber-100">No update in {issue.days_since_contact} days. Draft a polite follow-up?</span>
          <Button size="sm" onClick={onFollowup} className="h-7 rounded-lg bg-amber-400/20 hover:bg-amber-400/30 text-amber-50 text-xs shrink-0"><Send size={12} className="mr-1" /> Draft</Button>
        </div>
      )}

      {updates.length > 0 && (
        <div className="space-y-1.5" data-testid={`maintenance-updates-${issue.issue_id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40 flex items-center gap-1.5"><ClipboardList size={12} /> Update log</p>
          {updates.map((u, i) => (
            <div key={i} className="text-xs text-white/70 pl-3 border-l border-white/15">
              <span className="text-white/40">{fmt(u.date)} — </span>{u.note}
            </div>
          ))}
        </div>
      )}

      {after.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">Resolution photos</p>
          <div className="mt-1.5 flex gap-2 flex-wrap">
            {after.map((p) => (
              <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer" className="h-14 w-14 rounded-lg overflow-hidden border border-white/10">
                <img src={fileUrl(p.storage_path)} alt="resolution" className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addUpdate()}
            placeholder="Log an update…" data-testid={`maintenance-update-input-${issue.issue_id}`}
            className="min-w-0 flex-1 h-9 rounded-lg bg-white/5 border border-white/15 px-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-brand" />
          <Button size="sm" onClick={addUpdate} disabled={busy || !note.trim()} data-testid={`maintenance-update-add-${issue.issue_id}`}
            className="h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </Button>
          <input ref={afterRef} type="file" accept="image/*" capture="environment" onChange={addAfterPhoto} className="hidden" data-testid={`maintenance-after-input-${issue.issue_id}`} />
          <Button size="sm" variant="outline" onClick={() => afterRef.current?.click()} disabled={uploading} title="Add resolution photo"
            data-testid={`maintenance-after-btn-${issue.issue_id}`} className="h-9 rounded-lg border-white/20 bg-white/5 text-white hover:bg-white/15">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
          </Button>
        </div>
      )}
    </div>
  );
}
